import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { endpoints } from '../config/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { extractDataFromResponse, processBlogsData } from '../utils/apiUtils';
import SimpleBlogCard from '../components/blog/SimpleBlogCard';
import { setDefaultSEO } from '../utils/seoUtils';
import { createSlug } from '../utils/urlUtils';
import { AdUnitBeforePost } from '../components/adsense';
import { scrollToTop } from '../utils/scrollUtils';
import './Blogs.css';

const Blogs = () => {
  console.log('Blogs component rendering...');
  const navigate = useNavigate();
  

  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTag, setSelectedTag] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const BLOGS_PER_PAGE = 15; // Set to 15 blogs per page
  const [searchParams] = useSearchParams();
  const categoryFromUrl = searchParams.get('category');
  const [totalBlogs, setTotalBlogs] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const fetchBlogs = async (params = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      // Use the current state values or the values passed in params
      const category = params.category !== undefined ? params.category : selectedCategory;
      const tag = params.tags !== undefined ? params.tags : selectedTag;
      const page = params.page !== undefined ? params.page : currentPage;
      
      const queryParams = {
        status: 'published',
        page: page,
        limit: BLOGS_PER_PAGE,
        ...(tag && { tags: tag }),
        ...(category && category !== 'all' && { category: category })
      };
      
      console.log('Fetching blogs with params:', queryParams);
      
      const response = await axios.get(endpoints.blogs.list, { params: queryParams });
      console.log('API Response:', response.data);

      // Extract and process blogs data using utility function
      const blogsData = extractDataFromResponse(response, 'data');
      
      // Extract pagination info from the response
      if (response.data?.data) {
        setTotalBlogs(response.data.data.total || 0);
        setHasMore(response.data.data.hasMore || false);
      }
      
      const processedBlogs = processBlogsData(blogsData).map(blog => {
        console.log('Raw blog data:', blog);
        console.log('Publication date field:', {
          publicationDate: blog.publicationDate,
          createdAt: blog.createdAt,
          updatedAt: blog.updatedAt,
          date: blog.date
        });

        // Handle undefined or null image - check multiple possible image fields
        let imageUrl = '/images/blog-placeholder.jpg'; // Default image path
        const possibleImageFields = ['heroImage', 'image', 'featuredImage', 'coverImage', 'thumbnail', 'imageUrl', 'img'];
        
        console.log('Checking image fields for blog:', blog.title);
        console.log('All blog fields:', Object.keys(blog));
        
        for (const field of possibleImageFields) {
          if (blog[field]) {
            console.log(`Field '${field}' value:`, blog[field], 'Type:', typeof blog[field]);
            if (typeof blog[field] === 'string' && blog[field].trim() !== '') {
              console.log(`Found image in field '${field}':`, blog[field]);
              imageUrl = blog[field];
              break;
            } else if (typeof blog[field] === 'object' && blog[field] !== null) {
              // Handle case where image might be an object with url property
              if (blog[field].url && typeof blog[field].url === 'string') {
                console.log(`Found image URL in object field '${field}':`, blog[field].url);
                imageUrl = blog[field].url;
                break;
              }
            }
          }
        }
        
        if (imageUrl === '/images/blog-placeholder.jpg') {
          console.log('No image found in any field, using fallback for blog:', blog.title);
        }

        const processedBlog = {
          _id: blog._id,
          title: blog.title,
          excerpt: blog.excerpt || blog.content?.substring(0, 150),
          authorName: blog.authorName,
          publicationDate: blog.publicationDate || blog.createdAt || blog.date,
          heroImage: imageUrl,
          commentsCount: blog.commentsCount || 0,
          tags: blog.tags || [],
          category: blog.category,
          slug: blog.slug || createSlug(blog.title)
        };

        console.log('Processed blog:', processedBlog);
        return processedBlog;
      });

      // Sort blogs by publication date in descending order (newest first)
      const sortedBlogs = processedBlogs.sort((a, b) => {
        const dateA = new Date(a.publicationDate);
        const dateB = new Date(b.publicationDate);
        return dateB - dateA;
      });

      console.log('Setting blogs state:', sortedBlogs);
      setBlogs(sortedBlogs);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      setError('Failed to fetch blogs. Please try again later.');
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  };


  const handleTagClick = (tag) => {
    setSelectedTag(tag);
    setCurrentPage(1); // Reset to first page when filtering by tag
    // Keep the current category when filtering by tag
    const categoryParam = selectedCategory === 'all' ? null : selectedCategory;
    fetchBlogs({ tags: tag, page: 1, category: categoryParam });
    // Scroll to top to show results
    scrollToTop();
  };

  const handleCategoryChange = (e) => {
    const category = e.target.value;
    setSelectedCategory(category);
    setCurrentPage(1); // Reset to first page when changing category
    // Use the new category value directly in the fetch
    const categoryParam = category === 'all' ? null : category;
    // Clear any selected tag when changing category
    setSelectedTag('');
    fetchBlogs({ category: categoryParam, page: 1, tags: null });
    // Scroll to top to show results
    scrollToTop();
  };

  // Since we're now using server-side pagination, we don't need to slice the blogs
  const currentBlogs = blogs;
  
  console.log('Current blogs array:', currentBlogs);
  console.log('Total blogs from API:', totalBlogs);
  console.log('Current page:', currentPage);
  console.log('Blogs per page:', BLOGS_PER_PAGE);
  console.log('Has more pages:', hasMore);

  const totalPages = Math.ceil(totalBlogs / BLOGS_PER_PAGE);

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages are less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Always show first page
      pageNumbers.push(1);
      
      // Calculate start and end of visible pages
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);
      
      // Add ellipsis after first page if needed
      if (start > 2) {
        pageNumbers.push('...');
      }
      
      // Add visible page numbers
      for (let i = start; i <= end; i++) {
        pageNumbers.push(i);
      }
      
      // Add ellipsis before last page if needed
      if (end < totalPages - 1) {
        pageNumbers.push('...');
      }
      
      // Always show last page
      pageNumbers.push(totalPages);
    }
    
    return pageNumbers;
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Fetch blogs for the new page with current filters
    const categoryParam = selectedCategory === 'all' ? null : selectedCategory;
    const tagParam = selectedTag || null;
    fetchBlogs({ page: pageNumber, category: categoryParam, tags: tagParam });
    // Scroll to top smoothly when page changes
    scrollToTop();
  };

  // Initial fetch and handle category from URL
  useEffect(() => {
    if (categoryFromUrl) {
      setSelectedCategory(categoryFromUrl);
      fetchBlogs({ category: categoryFromUrl, page: 1 });
    } else {
      fetchBlogs({ page: 1 });
    }
    setDefaultSEO('Blog');
  }, [categoryFromUrl]);

  console.log('Blogs component state:', { loading, error, blogsCount: blogs.length, totalBlogs, currentPage });

  if (loading) {
    console.log('Showing loading spinner...');
    return <LoadingSpinner />;
  }

  if (error) {
    console.log('Showing error:', error);
    return (
      <div className="error-container">
        <h3>Error</h3>
        <p>{error}</p>
        <button onClick={() => fetchBlogs({ page: 1 })} className="retry-button">
          Try Again
        </button>
      </div>
    );
  }

  try {
    return (
      <div className="blogs-container">
        {/* SEO Meta Tags - Set via setDefaultSEO in useEffect */}
        
        <div className="blogs-layout">
        <main className="blogs-main">
          <div className="blogs-header">
            <h1>Latest Blog Posts</h1>
            <div className="search-filters">
              <select 
                value={selectedCategory}
                onChange={handleCategoryChange}
                className="category-select"
              >
                <option value="all">All Categories</option>
                <option value="blog">Blog</option>
                <option value="walk in">Walk In</option>
                <option value="internship">Internship</option>
                <option value="tutorials">Tutorials</option>
                <option value="jobs">Jobs</option>
              </select>
            </div>

            {selectedTag && (
              <div className="selected-tag-indicator">
                Showing posts tagged with: <span>{selectedTag}</span>
                <button 
                  onClick={() => {
                    setSelectedTag('');
                    fetchBlogs();
                  }}
                  className="clear-tag"
                >
                  ×
                </button>
              </div>
            )}
          </div>

          {/* Ad Before Blog Grid */}
          <AdUnitBeforePost />

          <div className="blogs-grid">
            {currentBlogs.length === 0 ? (
              <div className="no-blogs">
                <h3>No blogs found</h3>
                <p>No articles available</p>
              </div>
            ) : (
              currentBlogs.map((blog, index) => {
                console.log('Rendering SimpleBlogCard with blog:', blog);
                return (
                  <React.Fragment key={blog._id}>
                    <SimpleBlogCard blog={blog} />
                    {/* Add ad after every 6 blogs */}
                    {(index + 1) % 6 === 0 && <AdUnitBeforePost />}
                  </React.Fragment>
                );
              })
            )}
          </div>

          <div className="pagination-container">
            <div className="pagination-info">
              Showing {((currentPage - 1) * BLOGS_PER_PAGE) + 1} - {Math.min(currentPage * BLOGS_PER_PAGE, totalBlogs)} of {totalBlogs} blogs
            </div>
            
            <div className="pagination">
              <button 
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="page-btn prev"
              >
                Previous
              </button>

              <div className="page-numbers">
                {getPageNumbers().map((number, index) => (
                  number === '...' ? (
                    <span key={`ellipsis-${index}`} className="ellipsis">...</span>
                  ) : (
                    <button
                      key={number}
                      onClick={() => handlePageChange(number)}
                      className={`page-btn number ${currentPage === number ? 'active' : ''}`}
                    >
                      {number}
                    </button>
                  )
                ))}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="page-btn next"
              >
                Next
              </button>
            </div>
          </div>
        </main>
        
        <aside className="blogs-sidebar">
          {/* Ad in Sidebar */}
          <AdUnitBeforePost />

          {/* Category Box */}
          <div className="sidebar-box categories-box">
            <h3>Categories</h3>
            <ul className="category-list">
              <li 
                className={selectedCategory === 'all' ? 'active' : ''}
                onClick={() => handleCategoryChange({ target: { value: 'all' }})}
              >
                All Categories
              </li>
              <li 
                className={selectedCategory === 'blog' ? 'active' : ''}
                onClick={() => handleCategoryChange({ target: { value: 'blog' }})}
              >
                Blog
              </li>
              <li 
                className={selectedCategory === 'walk in' ? 'active' : ''}
                onClick={() => handleCategoryChange({ target: { value: 'walk in' }})}
              >
                Walk In
              </li>
              <li 
                className={selectedCategory === 'internship' ? 'active' : ''}
                onClick={() => handleCategoryChange({ target: { value: 'internship' }})}
              >
                Internship
              </li>
              <li 
                className={selectedCategory === 'tutorials' ? 'active' : ''}
                onClick={() => handleCategoryChange({ target: { value: 'tutorials' }})}
              >
                Tutorials
              </li>
              <li 
                className={selectedCategory === 'jobs' ? 'active' : ''}
                onClick={() => handleCategoryChange({ target: { value: 'jobs' }})}
              >
                Jobs
              </li>
            </ul>
          </div>

          <div className="career-slogan">
            <h2>One Click To your Career</h2>
            <p>Find Your Fit</p>
          </div>
          <div className="recent-posts">
            <h3>Recent Posts</h3>
            {blogs.slice(0, 4).map(blog => (
              <div key={blog._id} className="recent-post-item">
                <Link to={`/blog/${blog.slug}`}>
                  <h4>{blog.title}</h4>
                  <p>{blog.excerpt}</p>
                </Link>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </div>
    );
  } catch (error) {
    console.error('Error rendering Blogs component:', error);
    return (
      <div className="error-container">
        <h3>Rendering Error</h3>
        <p>Something went wrong while rendering the blogs page.</p>
        <button onClick={() => window.location.reload()} className="retry-button">
          Reload Page
        </button>
      </div>
    );
  }
};

export default Blogs;
